import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {useListViewVariant} from '@github-ui/list-view/ListViewVariantContext'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {CommentIcon} from '@primer/octicons-react'
import {Suspense} from 'react'
import {graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import {QUERY_FIELDS} from '../constants/queries'
import {TEST_IDS} from '../constants/test-ids'
import type {IssueItemMetadata$key} from './__generated__/IssueItemMetadata.graphql'
import type {IssueRowSecondaryQuery} from './__generated__/IssueRowSecondaryQuery.graphql'
import {Assignees} from './assignees/Assignees'
import {ClosedByPullRequestsReferences} from './closed_by_pull_requests_references/ClosedByPullRequestsReferences'
import issueItemStyles from './IssueItem.module.css'
import styles from './IssueItemMetadata.module.css'
import {IssuesIndexSecondaryGraphqlQuery} from './IssueRow'
import {Reactions} from './Reactions'

export type IssueItemMetadataProps = {
  itemKey: IssueItemMetadata$key
  showAssignees?: boolean
  showCommentCount?: boolean
  showCommentZeroCount?: boolean
  showLinkedPullRequests?: boolean
  /**
   * Href getter for the metadata badge links
   * @param type - metadata type
   * @param name - name of the metadata
   * @returns URL to the metadata
   */
  getMetadataHref: (type: keyof typeof QUERY_FIELDS, name: string) => string
  reactionEmojiToDisplay?: {reaction: string; reactionEmoji: string}
}

export type LazyIssueItemMetadataProps = Omit<IssueItemMetadataProps, 'itemKey'> & {
  issueId: string
  metadataRef?: PreloadedQuery<IssueRowSecondaryQuery> | null
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueItemMetadataQuery = graphql`
  fragment IssueItemMetadata on Issue
  @argumentDefinitions(
    assigneePageSize: {type: "Int!", defaultValue: 10}
    includeReactions: {type: "Boolean!", defaultValue: false}
  ) {
    id
    # Used to check if reactions were loaded
    reactionGroups @include(if: $includeReactions) {
      __typename
    }
    ... on Reactable @include(if: $includeReactions) {
      ...Reactions @dangerously_unaliased_fixme @include(if: $includeReactions)
    }
    totalCommentsCount
    ...Assignees @arguments(assigneePageSize: $assigneePageSize)
    ...ClosedByPullRequestsReferences
  }
`

export function LazyIssueItemMetadata({metadataRef, ...props}: LazyIssueItemMetadataProps) {
  if (metadataRef === null) return null
  // We need this to render the loading state in ssr
  if (!metadataRef) return <LoadingMetadata {...props} />

  return (
    <Suspense fallback={<LoadingMetadata {...props} />}>
      <LazyIssueMetadataFetched {...props} metadataRef={metadataRef} />
    </Suspense>
  )
}

export function LoadingMetadata({
  showAssignees,
  showCommentCount = true,
  reactionEmojiToDisplay,
  showLinkedPullRequests = false,
}: Partial<Omit<IssueItemMetadataProps, 'itemKey'>>) {
  const showReactions = !!reactionEmojiToDisplay?.reaction
  const isVisualizationEnabled =
    isFeatureEnabled('issue_cca_visualization') ||
    (isFeatureEnabled('copilot_mission_control_decoupled_mode') &&
      isFeatureEnabled('issue_cca_visualization_decoupled_mode'))
  return (
    <>
      {showLinkedPullRequests && (
        <ListItemMetadata data-testid={TEST_IDS.listRowLinkedPullRequests} className={styles.ListItemMetadata_0}>
          <LoadingSkeleton variant="pill" width="xl" />
        </ListItemMetadata>
      )}
      <ListItemMetadata data-testid={TEST_IDS.listRowComments} className={styles.ListItemMetadata_0}>
        {showCommentCount && <LoadingSkeleton variant="pill" width="xl" />}
      </ListItemMetadata>
      {showReactions && (
        <ListItemMetadata className={styles.ListItemMetadata_0}>
          <LoadingSkeleton variant="pill" width="xl" />
        </ListItemMetadata>
      )}
      {showAssignees && isVisualizationEnabled && (
        <ListItemMetadata
          data-testid={TEST_IDS.listRowAssignedAgents}
          alignment="right"
          className={styles.ListItemMetadata_0}
        >
          <LoadingSkeleton variant="pill" width="lg" />
        </ListItemMetadata>
      )}
      {showAssignees && (
        <ListItemMetadata
          data-testid={TEST_IDS.listRowAssignees}
          alignment="right"
          className={styles.ListItemMetadata_0}
        >
          <LoadingSkeleton variant="pill" width="lg" />
        </ListItemMetadata>
      )}
    </>
  )
}

function LazyIssueMetadataFetched({
  issueId,
  metadataRef,
  ...props
}: Omit<LazyIssueItemMetadataProps, 'metadataRef'> & {metadataRef: PreloadedQuery<IssueRowSecondaryQuery>}) {
  const {nodes} = usePreloadedQuery<IssueRowSecondaryQuery>(IssuesIndexSecondaryGraphqlQuery, metadataRef)
  const issueNode = nodes?.find(node => node?.id === issueId)

  if (!issueNode) return <LoadingMetadata {...props} />

  return <IssueItemMetadata itemKey={issueNode} {...props} />
}

export const IssueItemMetadata = ({
  itemKey,
  getMetadataHref,
  reactionEmojiToDisplay,
  showAssignees,
  showCommentCount = true,
  showCommentZeroCount = false,
  showLinkedPullRequests = false,
}: IssueItemMetadataProps) => {
  const {variant} = useListViewVariant()
  const data = useFragment(IssueItemMetadataQuery, itemKey)
  const showReactions = !!reactionEmojiToDisplay?.reaction
  const isVisualizationEnabled =
    isFeatureEnabled('issue_cca_visualization') ||
    (isFeatureEnabled('copilot_mission_control_decoupled_mode') &&
      isFeatureEnabled('issue_cca_visualization_decoupled_mode'))

  if (data.totalCommentsCount === undefined) {
    return (
      <LoadingMetadata
        reactionEmojiToDisplay={reactionEmojiToDisplay}
        showCommentCount
        showLinkedPullRequests
        showAssignees
      />
    )
  }

  const commentAriaLabel =
    data?.totalCommentsCount && data.totalCommentsCount > 0
      ? ` ${data.totalCommentsCount} comment${data.totalCommentsCount > 1 ? 's' : ''};`
      : ''

  // Feature flag graduated: issues_react_remove_placeholders
  // This logic better reflects the intended behaviour of showCommentZeroCount -
  // if true, show the comment count even if it's zero
  const commentCount = data.totalCommentsCount ?? 0
  const showCommentCountMetadata = showCommentCount && (showCommentZeroCount || commentCount > 0)

  return (
    <>
      {data && showLinkedPullRequests && (
        <ListItemMetadata data-testid={TEST_IDS.listRowLinkedPullRequests} className={styles.ListItemMetadata_0}>
          <ClosedByPullRequestsReferences issueId={data.id} closedByPullRequestsReferencesKey={data} />
        </ListItemMetadata>
      )}
      <ListItemMetadata
        aria-label={commentAriaLabel}
        data-testid={TEST_IDS.listRowComments}
        className={styles.ListItemMetadata_0}
      >
        {showCommentCountMetadata && (
          <div className={issueItemStyles.commentCountContainer}>
            <CommentIcon size={16} /> <span className="ml-1">{commentCount}</span>
            <span className="sr-only">{commentCount === 1 ? ' comment' : ' comments'}</span>
          </div>
        )}
      </ListItemMetadata>
      {showReactions && (
        <ListItemMetadata className={styles.ListItemMetadata_0}>
          {data.reactionGroups && (
            <Reactions
              dataKey={data}
              reactionEmojiToDisplay={reactionEmojiToDisplay}
              showCompactDensity={variant === 'compact'}
            />
          )}
        </ListItemMetadata>
      )}
      {showAssignees && data && isVisualizationEnabled && (
        <ListItemMetadata
          data-testid={TEST_IDS.listRowAssignedAgents}
          alignment="right"
          className={styles.ListItemMetadata_0}
        >
          <Assignees
            assigneeskey={data}
            filter="agents"
            getAssigneeHref={assignee => getMetadataHref(QUERY_FIELDS.assignee, assignee)}
          />
        </ListItemMetadata>
      )}
      {showAssignees && data && (
        <ListItemMetadata
          data-testid={TEST_IDS.listRowAssignees}
          alignment="right"
          className={styles.ListItemMetadata_0}
        >
          <Assignees
            assigneeskey={data}
            filter={isVisualizationEnabled ? 'users' : undefined}
            getAssigneeHref={assignee => getMetadataHref(QUERY_FIELDS.assignee, assignee)}
          />
        </ListItemMetadata>
      )}
    </>
  )
}
